import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DaumPostcode from 'react-daum-postcode';
import * as SOL from "../../styles/solda/StyledComponentSol";
import { baseUrl, getAccountBusinessProfile, insertAccountBusinessProfile, updateAccountBusinessProfile } from "../../Axios";
import ModalClose from "../Modal/ModalClose";
import ModalYesClose from "../Modal/ModalYesClose";
import ModalLoading from "../Modal/ModalLoading";

const BusinessProfileEdit: FC = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    account_business_profile_ID: 0,
    account_business_profile_company_name: '',
    account_business_profile_invoice_name: '',
    account_business_profile_business_number: '',
    account_business_profile_logo_img: '',
    account_business_profile_representative_name: '',
    account_business_profile_zip_code: '',
    account_business_profile_road_address: '',
    account_business_profile_detail_address: '',
    account_business_profile_phone_number: '',
    account_business_profile_fax_number: '',
    account_business_profile_email: '',
    account_business_profile_bank_name: '',
    account_business_profile_bank_holder: '',
    account_business_profile_bank_account_number: '',
  });
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [isInputMode, setIsInputMode] = useState<boolean>(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isReadingFile, setIsReadingFile] = useState<boolean>(false);
  const [profileSnapshot, setProfileSnapshot] = useState<typeof profileData | null>(null);
  const [logoPreviewSnapshot, setLogoPreviewSnapshot] = useState<string>('');

  const [isModalCloseOpen, setIsModalCloseOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<{text: string; onCloseCallback?: () => void}>({text: ''});
  const [isModalYesCloseOpen, setIsModalYesCloseOpen] = useState<boolean>(false);
  const [modalYesCloseContent, setModalYesCloseContent] = useState({
    text: '',
    confirmText: '',
    onConfirm: () => {}
  });

  const loadProfileData = () => {
    getAccountBusinessProfile().then(result => {
      if (result.success === false && result.message === 'session_invalid') {
        setModalContent({
          text: "고객 정보가 조회 되지 않습니다. 다시로그인해주세요.",
          onCloseCallback: () => {
            localStorage.removeItem('customer_ID_encrypted');
            localStorage.removeItem('alc_logo_img');
            navigate('/');
          },
        });
        setIsModalCloseOpen(true);
        return;
      }
      if (result.business_profile && result.business_profile.length > 0) {
        setProfileData(prev => ({
          ...prev,
          ...result.business_profile[0],
        }));
        const logoPath = result.business_profile[0].account_business_profile_logo_img;
        if (logoPath) {
          setLogoPreview(logoPath.startsWith('http') ? logoPath : `${baseUrl}${logoPath}`);
        } else {
          setLogoPreview('');
        }
        setLogoFile(null);
        setHasProfile(true);
        setIsInputMode(false);
      } else {
        setHasProfile(false);
      }
    });
  };

  useEffect(() => {
    loadProfileData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setProfileData({
      ...profileData,
      [field]: value,
    });
  };

  const showAlert = (text: string, onCloseCallback?: () => void) => {
    setModalContent({text, onCloseCallback});
    setIsModalCloseOpen(true);
  };

  const handleBusinessNumberChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    let formatted = digits;
    if (digits.length > 5) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    handleInputChange('account_business_profile_business_number', formatted);
  };

  const isValidBusinessNumber = (value: string) => /^\d{3}-\d{2}-\d{5}$/.test(value);

  const handlePhoneNumberChange = (field: string, value: string) => {
    const digits = value.replace(/\D/g, '');
    let formatted = digits;
    if (digits.startsWith('02')) {
      const limited = digits.slice(0, 10);
      if (limited.length <= 2) {
        formatted = limited;
      } else if (limited.length <= 5) {
        formatted = `${limited.slice(0, 2)}-${limited.slice(2)}`;
      } else if (limited.length <= 9) {
        formatted = `${limited.slice(0, 2)}-${limited.slice(2, 5)}-${limited.slice(5)}`;
      } else {
        formatted = `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6)}`;
      }
    } else {
      const limited = digits.slice(0, 11);
      if (limited.length <= 3) {
        formatted = limited;
      } else if (limited.length <= 6) {
        formatted = `${limited.slice(0, 3)}-${limited.slice(3)}`;
      } else if (limited.length <= 10) {
        formatted = `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
      } else {
        formatted = `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
      }
    }
    handleInputChange(field, formatted);
  };

  const isValidPhoneNumber = (value: string) => /^\d{2,3}-\d{3,4}-\d{4}$/.test(value);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleAccountNumberChange = (value: string) => {
    const filtered = value.replace(/[^\d-]/g, '');
    handleInputChange('account_business_profile_bank_account_number', filtered);
  };

  const handleAddressComplete = (data: any) => {
    setProfileData(prev => ({
      ...prev,
      account_business_profile_zip_code: data.zonecode,
      account_business_profile_road_address: data.roadAddress === '' ? data.autoRoadAddress : data.roadAddress,
    }));
    setIsAddressModalOpen(false);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
      const maxSize = 10 * 1024 * 1024;
      if (!allowedTypes.includes(file.type)) {
        showAlert("PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다.");
        e.target.value = '';
        return;
      }
      if (file.size > maxSize) {
        showAlert("파일 용량은 10MB 이하만 업로드 가능합니다.");
        e.target.value = '';
        return;
      }
      setLogoFile(file);
      setIsReadingFile(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
        setIsReadingFile(false);
      };
      reader.onerror = () => {
        setIsReadingFile(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const requestUpdateBusinessProfile = () => {
    setIsSaving(true);
    updateAccountBusinessProfile(
      profileData.account_business_profile_ID,
      profileData.account_business_profile_company_name,
      profileData.account_business_profile_invoice_name,
      profileData.account_business_profile_business_number,
      profileData.account_business_profile_representative_name,
      profileData.account_business_profile_zip_code,
      profileData.account_business_profile_road_address,
      profileData.account_business_profile_detail_address,
      profileData.account_business_profile_phone_number,
      profileData.account_business_profile_fax_number,
      profileData.account_business_profile_email,
      profileData.account_business_profile_bank_name,
      profileData.account_business_profile_bank_holder,
      profileData.account_business_profile_bank_account_number,
      logoFile,
    ).then(result => {
      setIsSaving(false);
      if (result.success === true) {
        setModalContent({
          text: "견적서 정보가 저장되었습니다.",
          onCloseCallback: () => {
            loadProfileData();
          },
        });
      } else {
        setModalContent({text: "알 수 없는 오류가 발생되었습니다. 관리자에게 문의해주시기 바랍니다."});
      }
      setIsModalCloseOpen(true);
    }).catch(() => {
      setIsSaving(false);
      setModalContent({text: "알 수 없는 오류가 발생되었습니다. 관리자에게 문의해주시기 바랍니다."});
      setIsModalCloseOpen(true);
    });
  };

  const validateInsertFields = (): boolean => {
    if (!profileData.account_business_profile_company_name.trim()) {
      showAlert("회사명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_invoice_name.trim()) {
      showAlert("인보이스명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_business_number.trim()) {
      showAlert("사업자등록번호를 입력해주세요.");
      return false;
    }
    if (!isValidBusinessNumber(profileData.account_business_profile_business_number)) {
      showAlert("사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)");
      return false;
    }
    if (!profileData.account_business_profile_representative_name.trim()) {
      showAlert("대표자명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_zip_code.trim()) {
      showAlert("우편번호를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_road_address.trim()) {
      showAlert("도로명주소를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_detail_address.trim()) {
      showAlert("상세주소를 입력해주세요.");
      return false;
    }
    if (!logoFile) {
      showAlert("로고 이미지를 선택해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_phone_number.trim()) {
      showAlert("전화번호를 입력해주세요.");
      return false;
    }
    if (!isValidPhoneNumber(profileData.account_business_profile_phone_number)) {
      showAlert("전화번호 형식이 올바르지 않습니다. (예: 010-1234-5678)");
      return false;
    }
    if (!profileData.account_business_profile_fax_number.trim()) {
      showAlert("팩스번호를 입력해주세요.");
      return false;
    }
    if (!isValidPhoneNumber(profileData.account_business_profile_fax_number)) {
      showAlert("팩스번호 형식이 올바르지 않습니다. (예: 02-1234-5678)");
      return false;
    }
    if (!profileData.account_business_profile_email.trim()) {
      showAlert("이메일을 입력해주세요.");
      return false;
    }
    if (!isValidEmail(profileData.account_business_profile_email)) {
      showAlert("이메일 형식이 올바르지 않습니다. (예: example@domain.com)");
      return false;
    }
    if (!profileData.account_business_profile_bank_name.trim()) {
      showAlert("은행명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_bank_holder.trim()) {
      showAlert("예금주를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_bank_account_number.trim()) {
      showAlert("계좌번호를 입력해주세요.");
      return false;
    }
    return true;
  };

  const validateUpdateFields = (): boolean => {
    if (!profileData.account_business_profile_company_name.trim()) {
      showAlert("회사명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_invoice_name.trim()) {
      showAlert("인보이스명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_business_number.trim()) {
      showAlert("사업자등록번호를 입력해주세요.");
      return false;
    }
    if (!isValidBusinessNumber(profileData.account_business_profile_business_number)) {
      showAlert("사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)");
      return false;
    }
    if (!profileData.account_business_profile_representative_name.trim()) {
      showAlert("대표자명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_zip_code.trim()) {
      showAlert("우편번호를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_road_address.trim()) {
      showAlert("도로명주소를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_detail_address.trim()) {
      showAlert("상세주소를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_phone_number.trim()) {
      showAlert("전화번호를 입력해주세요.");
      return false;
    }
    if (!isValidPhoneNumber(profileData.account_business_profile_phone_number)) {
      showAlert("전화번호 형식이 올바르지 않습니다. (예: 010-1234-5678)");
      return false;
    }
    if (!profileData.account_business_profile_fax_number.trim()) {
      showAlert("팩스번호를 입력해주세요.");
      return false;
    }
    if (!isValidPhoneNumber(profileData.account_business_profile_fax_number)) {
      showAlert("팩스번호 형식이 올바르지 않습니다. (예: 02-1234-5678)");
      return false;
    }
    if (!profileData.account_business_profile_email.trim()) {
      showAlert("이메일을 입력해주세요.");
      return false;
    }
    if (!isValidEmail(profileData.account_business_profile_email)) {
      showAlert("이메일 형식이 올바르지 않습니다. (예: example@domain.com)");
      return false;
    }
    if (!profileData.account_business_profile_bank_name.trim()) {
      showAlert("은행명을 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_bank_holder.trim()) {
      showAlert("예금주를 입력해주세요.");
      return false;
    }
    if (!profileData.account_business_profile_bank_account_number.trim()) {
      showAlert("계좌번호를 입력해주세요.");
      return false;
    }
    return true;
  };

  const requestInsertBusinessProfile = () => {
    setIsSaving(true);
    insertAccountBusinessProfile(
      profileData.account_business_profile_company_name,
      profileData.account_business_profile_invoice_name,
      profileData.account_business_profile_business_number,
      profileData.account_business_profile_representative_name,
      profileData.account_business_profile_zip_code,
      profileData.account_business_profile_road_address,
      profileData.account_business_profile_detail_address,
      profileData.account_business_profile_phone_number,
      profileData.account_business_profile_fax_number,
      profileData.account_business_profile_email,
      profileData.account_business_profile_bank_name,
      profileData.account_business_profile_bank_holder,
      profileData.account_business_profile_bank_account_number,
      logoFile,
    ).then(result => {
      setIsSaving(false);
      if (result.success === true) {
        setModalContent({
          text: "견적서 정보가 저장되었습니다.",
          onCloseCallback: () => {
            loadProfileData();
          },
        });
      } else {
        setModalContent({text: "알 수 없는 오류가 발생되었습니다. 관리자에게 문의해주시기 바랍니다."});
      }
      setIsModalCloseOpen(true);
    }).catch(() => {
      setIsSaving(false);
      setModalContent({text: "알 수 없는 오류가 발생되었습니다. 관리자에게 문의해주시기 바랍니다."});
      setIsModalCloseOpen(true);
    });
  };

  return (
    <>
      <ModalClose
        isOpen={isModalCloseOpen}
        onClose={() => {
          setIsModalCloseOpen(false);
          modalContent.onCloseCallback?.();
        }}
        text={modalContent.text}
      />
      <ModalYesClose
        isOpen={isModalYesCloseOpen}
        onClose={() => setIsModalYesCloseOpen(false)}
        onConfirm={modalYesCloseContent.onConfirm}
        confirmText={modalYesCloseContent.confirmText}
        text={modalYesCloseContent.text}
      />
      <ModalLoading isOpen={isSaving} />
      {hasProfile === false && !isInputMode ? (
        <SOL.MyPageComponentContainer style={{marginBottom: "145px"}}>
          <div className="text-box">
            <h3 className="title">견적서 정보 수정</h3>
          </div>
          <div className="horizontal" />
          <div className="text-box">
            <span className="content">저장된 견적서 정보가 없습니다.</span>
          </div>
          <div className="save-box">
            <button onClick={() => {
              setProfileSnapshot({...profileData});
              setLogoPreviewSnapshot(logoPreview);
              setIsInputMode(true);
            }}>
              견적서 정보 입력
            </button>
          </div>
        </SOL.MyPageComponentContainer>
      ) : hasProfile === true && !isInputMode ? (
        <div style={{marginBottom: "145px"}}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px"}}>
            <h3 style={{fontSize: "24px", fontWeight: 700, margin: 0}}>견적서 정보</h3>
            <button
              type="button"
              onClick={() => {
                setProfileSnapshot({...profileData});
                setLogoPreviewSnapshot(logoPreview);
                setIsInputMode(true);
              }}
              style={{
                padding: "8px 20px",
                border: "1px solid #000",
                background: "#fff",
                fontSize: "14px",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              수정
            </button>
          </div>

          <div className="border border-light_grey border-solid" style={{overflowX: "auto", maxWidth: "100%"}}>
            <SOL.PDFComponent>
              <div className="pdf-headline">
                <div className="title">
                  <h3>견적서 미리보기</h3>
                </div>
                <div className="logo-img-box">
                  {logoPreview && <img src={logoPreview} alt="업체 로고" />}
                </div>
              </div>
              <div className="pdf-headline">
                <span>
                  {(() => {
                    const d = new Date();
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  })()}
                </span>
                <span>page 1</span>
              </div>
              <div className="company-info-box">
                <div className="channel b2b">
                  <p className="label font-semibold">Invoice to</p>
                  <p className="account-name">
                    {profileData.account_business_profile_invoice_name}
                  </p>
                </div>
                <div className="channel ambience">
                  <p className="label font-semibold">Company</p>
                  <p className="name">
                    {profileData.account_business_profile_company_name}
                  </p>
                  <div className="content">
                    <p>등록번호 : {profileData.account_business_profile_business_number}</p>
                    <p>대표자 : {profileData.account_business_profile_representative_name}</p>
                    <p>
                      주소 :
                      {profileData.account_business_profile_zip_code && ` (${profileData.account_business_profile_zip_code})`}
                      {profileData.account_business_profile_road_address && ` ${profileData.account_business_profile_road_address}`}
                      {profileData.account_business_profile_detail_address && ` ${profileData.account_business_profile_detail_address}`}
                    </p>
                    <p>Tel : {profileData.account_business_profile_phone_number}&nbsp;&nbsp;&nbsp;Fax : {profileData.account_business_profile_fax_number}</p>
                    <p>이메일 : {profileData.account_business_profile_email}</p>
                  </div>
                </div>
              </div>
              <SOL.CartContainer style={{margin: 0}}>
                <div className="cart-items">
                  <div className="cart-item-header border-t-2 border-b border-black border-solid">
                    <div className="font-medium px-2">NO</div>
                    <div className="cart-image"></div>
                    <div className='cart-details font-medium'>제품정보</div>
                    <div className='cart-price font-medium'>소비자가 / 공급가</div>
                    <div className='cart-quantity font-medium'>수량</div>
                    <div className='cart-total font-medium'>합계</div>
                    <div className='cart-vat font-medium'>부가세</div>
                  </div>
                </div>
              </SOL.CartContainer>
              <div style={{
                minHeight: "400px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d9d9d9",
                fontSize: "20px",
                letterSpacing: "0.05em",
              }}>
                견적서 미리보기
              </div>
              <div className="price-info">
                <div className="company-info-box price-box">
                  <div className="channel" />
                  <div className="channel">
                    <div className="price-general">
                      <div className="label">기존 공급가 합계</div>
                      <span className="price-text">-</span>
                    </div>
                    <div className="middle-line" />
                    <div className="price-general">
                      <div className="label">부가세</div>
                      <span className="price-text">-</span>
                    </div>
                    <div className="middle-line" />
                    <div className="price-general">
                      <div className="label">프로모션 할인가</div>
                      <span className="price-text">-</span>
                    </div>
                    <div className="middle-line" />
                    <div className="price-general final">
                      <div className="label">
                        최종공급가
                        <span className="description">&nbsp;(부가세포함)</span>
                      </div>
                      <span className="price-text">-</span>
                    </div>
                  </div>
                </div>
                <div
                  className="company-info-box"
                  style={{borderWidth: 0, margin: 0, paddingTop: "10px"}}
                >
                  <div className="channel" />
                  <div className="channel">
                    <span className="payment-place-label">납부처</span>
                    <span className="payment-place-content">
                      {profileData.account_business_profile_bank_holder} | {profileData.account_business_profile_bank_name} {profileData.account_business_profile_bank_account_number}
                    </span>
                  </div>
                </div>
              </div>
            </SOL.PDFComponent>
          </div>
        </div>
      ) : isInputMode && (
        <SOL.MyPageComponentContainer style={{marginBottom: "145px"}}>
          <div className="text-box" style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <h3 className="title" style={{margin: 0}}>{hasProfile ? "견적서 정보 수정" : "견적서 정보 입력"}</h3>
            <button
              type="button"
              onClick={() => {
                if (profileSnapshot) {
                  setProfileData(profileSnapshot);
                  setLogoPreview(logoPreviewSnapshot);
                }
                setLogoFile(null);
                setIsInputMode(false);
              }}
              style={{
                padding: "8px 20px",
                border: "1px solid #000",
                background: "#fff",
                fontSize: "14px",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              뒤로가기
            </button>
          </div>

          <div className="horizontal" />

          <div className="text-box">
            <h5 className="sub-title">회사 정보</h5>
            <div className="flex-text-box">
              <span className="label">회사명</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={30}
                value={profileData.account_business_profile_company_name}
                onChange={(e) => handleInputChange('account_business_profile_company_name', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">인보이스 명</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={30}
                placeholder="예: 회사명과 동일"
                value={profileData.account_business_profile_invoice_name}
                onChange={(e) => handleInputChange('account_business_profile_invoice_name', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">사업자등록번호</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                placeholder="예: 123-45-67890"
                value={profileData.account_business_profile_business_number}
                onChange={(e) => handleBusinessNumberChange(e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">대표자명</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={20}
                value={profileData.account_business_profile_representative_name}
                onChange={(e) => handleInputChange('account_business_profile_representative_name', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">우편번호</span>
              <input
                type="text"
                style={{width: "120px", height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9", marginRight: "8px"}}
                value={profileData.account_business_profile_zip_code}
                readOnly
              />
              <button
                type="button"
                style={{
                  height: "40px",
                  padding: "0 12px",
                  outline: "1px solid #d9d9d9",
                  background: "#fff",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                }}
                onClick={() => setIsAddressModalOpen(!isAddressModalOpen)}
              >
                {isAddressModalOpen ? "닫기" : "우편번호 검색"}
              </button>
            </div>
            {isAddressModalOpen && (
              <div style={{margin: "10px 0"}}>
                <DaumPostcode onComplete={handleAddressComplete} autoClose={false} />
              </div>
            )}
            <div className="flex-text-box">
              <span className="label">도로명주소</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                value={profileData.account_business_profile_road_address}
                readOnly
              />
            </div>
            <div className="flex-text-box">
              <span className="label">상세주소</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={50}
                value={profileData.account_business_profile_detail_address}
                onChange={(e) => handleInputChange('account_business_profile_detail_address', e.target.value)}
              />
            </div>
          </div>

          <div className="horizontal" />

          <div className="text-box">
            <h5 className="sub-title">로고 이미지</h5>
            <div style={{marginTop: "15px", display: "flex", alignItems: "flex-start", gap: "30px"}}>
              <div>
                {logoPreview && (
                  <div style={{marginBottom: "10px", width: "60px", height: "61px", display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <img
                      src={logoPreview}
                      alt="업체 로고"
                      style={{maxWidth: "60px", maxHeight: "61px", objectFit: "contain"}}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                  onClick={(e) => {
                    (e.currentTarget as HTMLInputElement).value = '';
                    setLogoFile(null);
                    setLogoPreview('');
                  }}
                  onChange={handleLogoChange}
                />
                {isReadingFile && (
                  <p style={{marginTop: "5px", fontSize: "12px", color: "#888"}}>
                    이미지 처리 중...
                  </p>
                )}
              </div>
              {hasProfile && profileData.account_business_profile_logo_img && (
                <div>
                  <p style={{fontSize: "12px", color: "#888", marginBottom: "5px"}}>기존 이미지</p>
                  <div style={{width: "60px", height: "61px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #ddd"}}>
                    <img
                      src={
                        profileData.account_business_profile_logo_img.startsWith('http')
                          ? profileData.account_business_profile_logo_img
                          : `${baseUrl}${profileData.account_business_profile_logo_img}`
                      }
                      alt="기존 로고"
                      style={{maxWidth: "60px", maxHeight: "61px", objectFit: "contain"}}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="horizontal" />

          <div className="text-box">
            <h5 className="sub-title">연락처 정보</h5>
            <div className="flex-text-box">
              <span className="label">전화번호</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                placeholder="예: 010-1234-5678"
                value={profileData.account_business_profile_phone_number}
                onChange={(e) => handlePhoneNumberChange('account_business_profile_phone_number', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">팩스번호</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                placeholder="예: 02-1234-5678"
                value={profileData.account_business_profile_fax_number}
                onChange={(e) => handlePhoneNumberChange('account_business_profile_fax_number', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">이메일</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={50}
                value={profileData.account_business_profile_email}
                onChange={(e) => handleInputChange('account_business_profile_email', e.target.value)}
              />
            </div>
          </div>

          <div className="horizontal" />

          <div className="text-box">
            <h5 className="sub-title">결제 정보</h5>
            <div className="flex-text-box">
              <span className="label">은행명</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={20}
                value={profileData.account_business_profile_bank_name}
                onChange={(e) => handleInputChange('account_business_profile_bank_name', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">예금주</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={20}
                value={profileData.account_business_profile_bank_holder}
                onChange={(e) => handleInputChange('account_business_profile_bank_holder', e.target.value)}
              />
            </div>
            <div className="flex-text-box">
              <span className="label">계좌번호</span>
              <input
                type="text"
                style={{flex: 1, height: "40px", padding: "0 10px", outline: "1px solid #d9d9d9"}}
                maxLength={25}
                value={profileData.account_business_profile_bank_account_number}
                onChange={(e) => handleAccountNumberChange(e.target.value)}
              />
            </div>
          </div>

          <div className="save-box">
            <button
              onClick={() => {
                if (hasProfile) {
                  if (!validateUpdateFields()) return;
                } else {
                  if (!validateInsertFields()) return;
                }
                setIsModalYesCloseOpen(true);
                setModalYesCloseContent({
                  text: hasProfile ? "변경된 사항을 저장하시겠습니까?" : "입력하신 견적서 정보를 저장하시겠습니까?",
                  confirmText: "저장",
                  onConfirm: () => {
                    setIsModalYesCloseOpen(false);
                    if (hasProfile) {
                      requestUpdateBusinessProfile();
                    } else {
                      requestInsertBusinessProfile();
                    }
                  }
                });
              }}
            >
              저장하기
            </button>
          </div>
        </SOL.MyPageComponentContainer>
      )}
    </>
  )
};

export default BusinessProfileEdit;
